import { MagnifyingGlass, Prohibit } from "@phosphor-icons/react"

import { Button } from "@tokenterminal/core/ui/button/Button"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"

import { Combobox } from "@tokenterminal/core/ui/samsCombobox/Combobox"
import { filterAndSortItems } from "@tokenterminal/core/ui/samsCombobox/searchableList/SearchableList"

import { useMetricSorting } from "hooks/useMetricSorting"
import { getMetricLabel } from "modules/metrics/getMetricLabel"
import { useMemo, useState } from "react"
import { type MetricConfiguration } from "utils/trpc"
import { getMetricSortingConfig } from "../../../utils/metricSortingConfig"

type AvailableMetric = Parameters<typeof getMetricLabel>[0] &
  Pick<MetricConfiguration, "metric_id" | "slug" | "short_title" | "categories">

export const MetricsDropdown = ({
  availableMetrics,
  selectedMetricId,
  onChangeSelectedMetricId,
  disabled,
  projectInfo,
  format = "long",
  disabledKeys,
  trigger,
}: {
  label?: string
  availableMetrics: Array<AvailableMetric>
  selectedMetricId: string
  onChangeSelectedMetricId: (metricId: string) => void
  disabled?: boolean
  projectInfo?: {
    symbol: string | null
  }
  format?: "short" | "long"
  disabledKeys?: Array<string>
  trigger?: React.ReactNode
}) => {
  const [search, setSearch] = useState("")

  // Create a metrics configuration dictionary for the sorting hook
  const metricsConfigurationListDic = useMemo(() => {
    const map = new Map<string, any>()
    availableMetrics.forEach((metric) => {
      map.set(metric.metric_id, metric)
    })
    return map
  }, [availableMetrics])

  // Group metrics by category
  const metricsByCategory = useMemo(() => {
    const result: Record<string, MetricConfiguration[]> = {}

    availableMetrics.forEach((metric) => {
      const category = metric.categories[0]?.toLowerCase() ?? "other"
      if (!result[category]) {
        result[category] = []
      }
      result[category].push(metric as MetricConfiguration)
    })

    return result
  }, [availableMetrics])

  // Use the metric sorting hook with config
  const { sortedMetricsByCategory, sortedCategories } = useMetricSorting(
    metricsByCategory,
    metricsConfigurationListDic,
    getMetricSortingConfig()
  )

  // Format metrics with labels for display
  const metricsWithFormattedLabel = useMemo(() => {
    const result: Array<{
      label: string
      value: string
      group: string
    }> = []

    sortedCategories.forEach((category) => {
      const metrics = sortedMetricsByCategory[category] || []
      metrics.forEach((metric) => {
        result.push({
          value: metric.metric_id,
          group: category,
          label:
            format === "short" && metric.short_title
              ? metric.short_title
              : getMetricLabel(metric, projectInfo),
        })
      })
    })

    return result
  }, [sortedMetricsByCategory, sortedCategories, format, projectInfo])

  const groupMetricsWithFormattedLabel = useMemo(() => {
    const groups = new Map<string, Array<{ label: string; value: string }>>()
    metricsWithFormattedLabel.forEach((metric) => {
      if (metric.group) {
        if (!groups.has(metric.group)) {
          groups.set(metric.group, [])
        }
        groups.get(metric.group)?.push(metric)
      }
    })

    return Array.from(groups.entries()).map(([group, metrics]) => ({
      label: group[0].toUpperCase() + group.slice(1),
      value: group,
      children: metrics,
    }))
  }, [metricsWithFormattedLabel])

  const filteredGroups = useMemo(() => {
    return groupMetricsWithFormattedLabel
      .map((group) => {
        const nonDisabledChildren = group.children.filter(
          (child) => !disabledKeys?.includes(child.value)
        )

        return {
          ...group,
          children:
            search.length > 0
              ? filterAndSortItems(
                  nonDisabledChildren,
                  search,
                  (child) => child.label
                )
              : nonDisabledChildren,
        }
      })
      .filter((group) => group.children.length > 0)
  }, [groupMetricsWithFormattedLabel, search, disabledKeys])

  const filteredDisabledGroups = useMemo(() => {
    return groupMetricsWithFormattedLabel
      .map((group) => ({
        ...group,
        children: filterAndSortItems(
          group.children.filter((child) => disabledKeys?.includes(child.value)),
          search,
          (child) => child.label
        ),
      }))
      .filter((group) => group.children.length > 0)
  }, [groupMetricsWithFormattedLabel, search, disabledKeys])

  const items = useMemo(() => {
    return [
      ...filteredGroups.map((group, index) => (
        <div key={group.value}>
          <Combobox.GroupHeader>{group.label}</Combobox.GroupHeader>
          {group.children.map((child) => (
            <Combobox.Item key={child.value} id={child.value}>
              {child.label}
              <Combobox.SelectionIndicator />
            </Combobox.Item>
          ))}
          {index < filteredGroups.length - 1 && <Combobox.Divider />}
        </div>
      )),
      ...(filteredGroups.length > 0 && filteredDisabledGroups.length > 0
        ? [<Combobox.Divider />]
        : []),
      ...filteredDisabledGroups.map((group) => (
        <div key={group.value + "-disabled"}>
          <Combobox.GroupHeader>{group.label}</Combobox.GroupHeader>
          {group.children.map((child) => (
            <Combobox.Item key={child.value} id={child.value} disabled>
              {child.label}
              <Icon as={Prohibit} />
            </Combobox.Item>
          ))}
        </div>
      )),
    ]
  }, [filteredGroups, filteredDisabledGroups])

  const handleMetricChange = (metricId: string | null) => {
    if (!metricId) return
    onChangeSelectedMetricId(metricId)
  }

  return (
    <Combobox
      drawerHeaderTitle="Select a metric"
      search={search}
      setSearch={setSearch}
      selectionMode="single"
      selectedId={selectedMetricId}
      onChangeSelectedId={handleMetricChange}
      trigger={
        trigger || (
          <Button variant="ghost" isDisabled={disabled}>
            <Icon as={MagnifyingGlass} />
            Search all metrics
          </Button>
        )
      }
      shouldFilter={false}
    >
      {items.length > 0 ? (
        items
      ) : (
        <Combobox.EmptyState>No metrics found</Combobox.EmptyState>
      )}
    </Combobox>
  )
}
